import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { auth } from '../lib/auth'

interface NicknameLoginModalProps {
  isOpen: boolean
  onClose: () => void
  onLogin: (nickname: string, avatar: string) => void
  onLogout?: () => void
}

export default function NicknameLoginModal({ isOpen, onClose, onLogin, onLogout }: NicknameLoginModalProps) {
  const [nickname, setNickname] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen) {
      setNickname('')
      setError('')
    }
  }, [isOpen])

  const handleLogin = async () => {
    if (!nickname.trim()) {
      setError('请输入昵称')
      return
    }

    if (nickname.trim().length < 1) {
      setError('昵称至少 1 个字符')
      return
    }

    if (nickname.trim().length > 32) {
      setError('昵称不能超过 32 个字符')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      // Call the RPC to get or create user profile
      const { error: rpcError } = await supabase.rpc('get_or_create_game_user', {
        p_nickname: nickname.trim()
      })

      if (rpcError) throw rpcError

      // Use auth module to login
      auth.login(nickname.trim(), '🧙‍♂️')

      onLogin(nickname.trim(), '🧙‍♂️')
    } catch (err) {
      console.error('Login error:', err)
      setError('登录失败，请重试')
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleLogin()
    }
  }

  const handleClose = () => {
    // 如果用户关闭弹窗，视为退出登录
    if (onLogout) {
      onLogout()
    }
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-[#2A1A40] rounded-2xl border border-[#5A4BFF]/35 w-full max-w-sm p-6 shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">登录账号</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-[#1A0F2A] rounded-lg transition-colors"
          >
            <X size={20} className="text-[#B0A8D0]" />
          </button>
        </div>

        {/* Content */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-[#B0A8D0] mb-2">
            昵称
          </label>
          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="请输入 1-32 个字符的昵称"
            className="w-full px-4 py-3 bg-[#1A0F2A] border-2 border-[#5A4BFF]/35 rounded-xl text-white focus:border-[#F5D74A] focus:outline-none placeholder-[#5A4BFF]/50"
            maxLength={32}
            autoFocus
          />

          {error && (
            <p className="text-red-400 text-sm mt-2">{error}</p>
          )}

          <div className="mt-5 p-4 bg-gradient-to-r from-[#F5D74A]/10 to-[#FFD700]/10 rounded-xl border-2 border-[#F5D74A]/40">
            <p className="text-sm text-[#F5D74A] font-bold mb-2 flex items-center gap-2">
              <span className="text-lg">⚠️</span>
              重要提醒
            </p>
            <p className="text-xs text-[#F5D74A]/90 leading-relaxed font-medium">
              该昵称将用于同步线上游戏和战绩数据，请确保与之前使用的昵称一致！
            </p>
          </div>
        </div>

        {/* Action Button */}
        <button
          onClick={handleLogin}
          disabled={isLoading}
          className="w-full py-3 bg-gradient-to-r from-[#F5D74A] to-[#FFD700] text-[#1A0F2A] font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 transition-all"
        >
          {isLoading ? '登录中...' : '开始游戏'}
        </button>
      </div>
    </div>
  )
}
